import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { Button, Modal, ModalContent } from '../common';
const MensDoublesEligibilityModal = ({
  open,
  onClose,
  player,
  sessionId,
  currentEligible = false,
  currentShadowElo = null,
  playersById = {},
  onSaved,
}) => {
  const defaultElo = player?.levels_elo_rating ?? 1200;

  const [enabled, setEnabled] = useState(currentEligible);
  const [shadowElo, setShadowElo] = useState(
    currentShadowElo != null ? String(currentShadowElo) : String(defaultElo)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset whenever modal opens for a (potentially different) player
  useEffect(() => {
    if (open) {
      setEnabled(currentEligible);
      setShadowElo(currentShadowElo != null ? String(currentShadowElo) : String(defaultElo));
      setError(null);
    }
  }, [open, currentEligible, currentShadowElo, defaultElo]);

  const playerName = player
    ? player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim()
    : 'Player';

  // Collect mens players ELOs from session roster for context panel
  const mensEloStats = useMemo(() => {
    const eloValues = Object.values(playersById)
      .filter((p) => (p?.gender || '').toLowerCase() === 'male' && p.levels_elo_rating != null)
      .map((p) => Number(p.levels_elo_rating));
    if (eloValues.length === 0) return null;
    const sorted = [...eloValues].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const median = sorted[Math.floor(sorted.length / 2)];
    return { min, median, max, count: eloValues.length };
  }, [playersById]);

  const shadowEloNum = parseInt(shadowElo, 10);
  const shadowEloValid =
    Number.isFinite(shadowEloNum) && shadowEloNum >= 400 && shadowEloNum <= 3000;

  const handleSave = async () => {
    if (enabled && !shadowEloValid) {
      setError('Shadow ELO must be a number between 400 and 3000.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from('session_players')
        .update({
          mens_doubles_eligible: enabled,
          mens_doubles_shadow_elo: enabled ? shadowEloNum : null,
        })
        .eq('session_id', sessionId)
        .eq('player_id', player.id);

      if (dbError) throw dbError;

      if (onSaved) {
        onSaved(player.id, enabled, enabled ? shadowEloNum : null);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save eligibility.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !player) return null;

  return (
    <Modal isOpen={open} onClose={onClose} closeOnBackdropClick={!saving}>
      <ModalContent
        size="medium"
        padding={false}
        className="flex min-h-0 flex-col overflow-hidden"
        aria-labelledby="mens-doubles-eligibility-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-medium px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <div id="mens-doubles-eligibility-title" className="text-base font-semibold text-text-primary">
              Mens doubles eligibility
            </div>
            {playerName ? (
              <div className="mt-0.5 truncate text-xs text-text-muted">{playerName}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="shrink-0 rounded-md p-1.5 text-text-muted transition hover:bg-surface-subtle hover:text-text-primary disabled:opacity-50"
            aria-label="Close"
          >
            <span aria-hidden className="text-xl leading-none">
              ×
            </span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              Mark <span className="font-medium text-text-primary">{playerName}</span> as eligible to
              play mens doubles for this session. She will still be eligible for womens doubles and
              mixed doubles.
            </p>

            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border-medium accent-primary"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="text-sm font-medium text-text-primary">
                Eligible for mens doubles this session
              </span>
            </label>

            {enabled && (
              <div className="space-y-1.5">
                <label
                  htmlFor="mens-shadow-elo"
                  className="block text-sm font-medium text-text-primary"
                >
                  Session shadow ELO
                </label>
                <p className="text-xs text-text-muted">
                  Used only for matchmaking balance. Does not affect club ratings.
                </p>
                <input
                  id="mens-shadow-elo"
                  type="number"
                  min={400}
                  max={3000}
                  step={10}
                  value={shadowElo}
                  onChange={(e) => setShadowElo(e.target.value)}
                  className="w-full rounded-md border border-border-medium bg-surface-subtle px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  aria-describedby="shadow-elo-context"
                />
                {shadowElo !== '' && !shadowEloValid && (
                  <p className="text-xs text-error">ELO must be between 400 and 3000.</p>
                )}

                {mensEloStats && (
                  <div
                    id="shadow-elo-context"
                    className="mt-2 rounded-md border border-border-light bg-surface-subtle p-3 text-xs text-text-secondary space-y-1"
                  >
                    <p className="font-medium text-text-primary">
                      Session male players — levels ELO
                    </p>
                    <div className="flex gap-4 tabular-nums">
                      <span>
                        Min: <span className="font-medium text-text-primary">{mensEloStats.min}</span>
                      </span>
                      <span>
                        Median:{' '}
                        <span className="font-medium text-text-primary">{mensEloStats.median}</span>
                      </span>
                      <span>
                        Max: <span className="font-medium text-text-primary">{mensEloStats.max}</span>
                      </span>
                    </div>
                    <p className="text-text-muted">
                      {mensEloStats.count} male player{mensEloStats.count !== 1 ? 's' : ''} in
                      session
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-error">{error}</p>}
          </div>
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-medium px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <Button
            type="button"
            variant="outline"
            size="small"
            className="w-full sm:w-auto"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="small"
            className="w-full sm:w-auto"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default MensDoublesEligibilityModal;
